import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  supplierService,
  Supplier,
  LedgerEntry,
} from "../../services/supplier.service";
import { showToast } from "../../lib/toast";

export function SupplierDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<
    "overview" | "ledger" | "payments"
  >("overview");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    paymentMethod: "BANK_TRANSFER" as const,
    reference: "",
    notes: "",
  });
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    if (id) {
      loadSupplier();
      loadSummary();
      loadLedger();
    }
  }, [id]);

  const loadSupplier = async () => {
    try {
      const data = await supplierService.getSupplier(id!);
      setSupplier(data);
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to load supplier");
      navigate("/suppliers");
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const data = await supplierService.getSupplierSummary(id!);
      setSummary(data);
    } catch (err) {
      console.error("Failed to load summary", err);
    }
  };

  const loadLedger = async () => {
    try {
      const data = await supplierService.getSupplierLedger(id!, { limit: 50 });
      setLedger(data.data);
    } catch (err) {
      console.error("Failed to load ledger", err);
    }
  };

  const handlePayment = async () => {
    if (paymentData.amount <= 0) {
      showToast.error("Amount must be greater than 0");
      return;
    }

    try {
      setPaymentLoading(true);
      await supplierService.recordPayment({
        supplierId: id!,
        amount: paymentData.amount,
        paymentMethod: paymentData.paymentMethod,
        reference: paymentData.reference || undefined,
        notes: paymentData.notes || undefined,
      });
      showToast.success("Payment recorded successfully");
      setShowPaymentModal(false);
      setPaymentData({
        amount: 0,
        paymentMethod: "BANK_TRANSFER",
        reference: "",
        notes: "",
      });
      loadSupplier();
      loadSummary();
      loadLedger();
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to record payment",
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-500">Loading supplier details...</div>
      </div>
    );
  }

  if (!supplier) {
    return null;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/suppliers")}
          className="text-blue-600 hover:text-blue-800 mb-2"
        >
          ← Back to Suppliers
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {supplier.name}
            </h1>
            <p className="text-sm text-gray-500">
              {supplier.code && `Code: ${supplier.code} | `}
              Phone: {supplier.phone}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowPaymentModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Record Payment
            </button>
            <button
              onClick={() => navigate(`/suppliers/${id}/edit`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Edit
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Outstanding Balance</div>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(Number(supplier.currentBalance))}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Purchases</div>
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(summary.stats.totalPurchases)}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Paid</div>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(summary.stats.totalPaid)}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Pending POs</div>
            <div className="text-2xl font-bold text-blue-600">
              {summary.stats.pendingPOs}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {["overview", "ledger", "payments"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
            <div className="space-y-3">
              <div>
                <span className="text-gray-500">Phone:</span>{" "}
                <span className="font-medium">{supplier.phone}</span>
              </div>
              {supplier.email && (
                <div>
                  <span className="text-gray-500">Email:</span>{" "}
                  <span className="font-medium">{supplier.email}</span>
                </div>
              )}
              {supplier.contactPerson && (
                <div>
                  <span className="text-gray-500">Contact Person:</span>{" "}
                  <span className="font-medium">{supplier.contactPerson}</span>
                </div>
              )}
              {supplier.address && (
                <div>
                  <span className="text-gray-500">Address:</span>{" "}
                  <span className="font-medium">
                    {supplier.address}
                    {supplier.city && `, ${supplier.city}`}
                    {supplier.state && `, ${supplier.state}`}
                    {supplier.pincode && ` - ${supplier.pincode}`}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Tax & Compliance</h3>
            <div className="space-y-3">
              {supplier.gstNumber && (
                <div>
                  <span className="text-gray-500">GST Number:</span>{" "}
                  <span className="font-medium">{supplier.gstNumber}</span>
                </div>
              )}
              {supplier.panNumber && (
                <div>
                  <span className="text-gray-500">PAN Number:</span>{" "}
                  <span className="font-medium">{supplier.panNumber}</span>
                </div>
              )}
              {supplier.drugLicenseNo && (
                <div>
                  <span className="text-gray-500">Drug License:</span>{" "}
                  <span className="font-medium">{supplier.drugLicenseNo}</span>
                </div>
              )}
              <div>
                <span className="text-gray-500">Credit Days:</span>{" "}
                <span className="font-medium">{supplier.creditDays} days</span>
              </div>
              <div>
                <span className="text-gray-500">Credit Limit:</span>{" "}
                <span className="font-medium">
                  {formatCurrency(Number(supplier.creditLimit))}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "ledger" && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Reference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Debit
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Credit
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {ledger.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(entry.date)}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        entry.type === "GRN"
                          ? "bg-blue-100 text-blue-800"
                          : entry.type === "RETURN"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-green-100 text-green-800"
                      }`}
                    >
                      {entry.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {entry.reference}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {entry.description}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-red-600">
                    {entry.debit ? formatCurrency(Number(entry.debit)) : "-"}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-green-600">
                    {entry.credit ? formatCurrency(Number(entry.credit)) : "-"}
                  </td>
                </tr>
              ))}
              {ledger.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "payments" && (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500">Payment history will be shown here.</p>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Record Payment</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount *
                </label>
                <input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) =>
                    setPaymentData((p) => ({
                      ...p,
                      amount: Number(e.target.value),
                    }))
                  }
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method
                </label>
                <select
                  value={paymentData.paymentMethod}
                  onChange={(e) =>
                    setPaymentData((p) => ({
                      ...p,
                      paymentMethod: e.target.value as any,
                    }))
                  }
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference (UTR/Cheque No.)
                </label>
                <input
                  type="text"
                  value={paymentData.reference}
                  onChange={(e) =>
                    setPaymentData((p) => ({ ...p, reference: e.target.value }))
                  }
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) =>
                    setPaymentData((p) => ({ ...p, notes: e.target.value }))
                  }
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePayment}
                disabled={paymentLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {paymentLoading ? "Recording..." : "Record Payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
